<template>
  <teleport to="#modal-container">
    <transition appear :duration="150" @after-enter="() => (showContent = true)">
      <div :class="['modal bg-base-300/90', { 'modal-open': showContent }]">
        <div
          v-if="showContent"
          ref="target"
          :class="[
            contentClass,
            'modal-box w-full overflow-y-auto rounded bg-white shadow-lg transition initial:p-5',
            {
              'max-w-6xl': size === 'xl',
              'max-w-4xl': size === 'large',
              'max-w-xl': size === 'medium',
              'max-w-md': size === 'small',
              'h-full': height === 'full',
            },
          ]"
        >
          <m-heading v-if="title" theme="h2" :position="titlePosition">{{ title }}</m-heading>
          <m-dot-loader v-if="loading" class="py-16" />

          <div v-else class="h-full">
            <slot name="header"></slot>
            <slot />
            <m-action-list v-if="$slots.actions" class="pt-3">
              <slot name="actions"></slot>
            </m-action-list>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { onClickOutside, onKeyStroke } from '@vueuse/core';
  import type { MHeading_PositionProp } from '@core/components/MHeading';
  import MDotLoader from '@core/components/MDotLoader/MDotLoader.vue';

  export interface Props {
    title?: string | null;
    titlePosition?: MHeading_PositionProp;
    form?: boolean;
    size?: 'small' | 'medium' | 'large' | 'xl';
    disableClickOutSide?: boolean;
    disableCloseOnEscape?: boolean;
    contentClass?: string;
    /**
     * Whether or not to show the loading indicator
     * Hides Content untile loading is complete
     */
    loading?: boolean;
    height?: 'defualt' | 'full';
  }
  export interface Emits {
    (e: 'close'): void;
    (e: 'submit'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'medium',
    form: true,
    title: null,
    titlePosition: 'start',
    disableClickOutSide: false,
    disableCloseOnEscape: false,
    loading: false,
    contentClass: undefined,
    height: 'defualt',
  });
  const emit = defineEmits<Emits>();

  const showContent = ref(false);
  const target = ref(null);

  onClickOutside(target, () => {
    if (props.disableClickOutSide) return;
    showContent.value = false;
    emit('close');
  });

  onKeyStroke('Escape', () => {
    if (props.disableCloseOnEscape) return;
    showContent.value = false;
    emit('close');
  });

  //const isLocked = useScrollLock(document.body, document.body.style.overflow === 'hidden');

  onMounted(() => {
    document.body.style.overflow = 'hidden';
    //isLocked.value = true;
  });

  onBeforeUnmount(() => {
    document.body.style.overflow = '';
    // isLocked.value = false;
  });
</script>
<style>
  .modal {
    @apply bg-customer-card bg-opacity-90;
  }
</style>
